import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { Building2, Check, ClipboardList, Plus, User, Users, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * Assign Task (por departamento, debajo de Department Updates): asigna una tarea a un
 * usuario o equipo → le llega al To Do (/todos), como DM en Messaging y como notificación.
 */
type Task = { id: string; title: string; note: string | null; due_date: string | null; priority: string; status: string; assigned_by: string | null; assigned_to: string | null; team_name?: string | null; department_name?: string | null; completed_by?: string | null };
type Target = { id: string; name: string };

export function ClaimTasksBox({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<Target[]>([]);
  const [teams, setTeams] = useState<Target[]>([]);
  const [departments, setDepartments] = useState<Target[]>([]);
  const [open, setOpen] = useState(false);
  const [assignee, setAssignee] = useState(''); // "user:<id>" | "team:<id>" | "dept:<id>"
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.get<{ tasks: Task[] }>(`/api/claims/${claimId}/tasks`).then((r) => setTasks(r.tasks)).catch(() => {});
  useEffect(() => {
    load();
    api.get<{ users: Target[]; teams: Target[]; departments?: Target[] }>('/api/team/mention-targets').then((r) => { setUsers(r.users || []); setTeams(r.teams || []); setDepartments(r.departments || []); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  async function assign() {
    if (!assignee || !title.trim()) return;
    const [kind, id] = assignee.split(':');
    setBusy(true);
    try {
      await api.post('/api/tasks', {
        claimId, title: title.trim(), dueDate: due || undefined,
        assignedToUserId: kind === 'user' ? id : undefined,
        assignedToTeamId: kind === 'team' ? id : undefined,
        assignedToDepartmentId: kind === 'dept' ? id : undefined,
      });
      setTitle(''); setDue(''); setAssignee(''); setOpen(false); await load();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  async function done(id: string) {
    try { await api.post(`/api/tasks/${id}/done`, {}); await load(); } catch { /* */ }
  }

  // El API devuelve `status` ('open' | 'done'), NO done_at: filtrar por done_at dejaba
  // `!undefined` = true y las hechas seguían a la vista aunque el check sí las cerraba.
  const openTasks = tasks.filter((tk) => tk.status !== 'done');
  const doneTasks = tasks.filter((tk) => tk.status === 'done');
  // Ver las tareas ya HECHAS (pedido de Manuel): colapsadas debajo, con quién las cerró.
  const [showDone, setShowDone] = useState(false);

  return (
    <div className="card" style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="muted" style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}><ClipboardList size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('task.boxTitle')}</span>
        {openTasks.length > 0 && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{openTasks.length}</span>}
        <span style={{ flex: 1 }} />
        <button className="btn ghost sm" onClick={() => setOpen((o) => !o)}>{open ? <X size={14} strokeWidth={1.75} /> : <><Plus size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('task.assign')}</>}</button>
      </div>

      {open && (
        <div style={{ display: 'grid', gap: 6, border: '1px solid var(--line)', borderRadius: 8, padding: 8 }}>
          <select className="input sm" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">{t('task.pickAssignee')}</option>
            {departments.length > 0 && <optgroup label={t('chat.toDoDepts')}>{departments.map((d) => <option key={`d${d.id}`} value={`dept:${d.id}`}>{d.name}</option>)}</optgroup>}
            {teams.length > 0 && <optgroup label={t('chat.toDoTeams')}>{teams.map((tm) => <option key={`t${tm.id}`} value={`team:${tm.id}`}>{tm.name}</option>)}</optgroup>}
            {users.length > 0 && <optgroup label={t('task.people')}>{users.map((u) => <option key={`u${u.id}`} value={`user:${u.id}`}>{u.name}</option>)}</optgroup>}
          </select>
          <input className="input sm" placeholder={t('task.whatToDo')} value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') assign(); }} />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <input className="input sm" type="date" style={{ width: 150 }} value={due} onChange={(e) => setDue(e.target.value)} />
            <button className="btn sm" disabled={busy || !assignee || !title.trim()} onClick={assign}>{t('task.assign')}</button>
          </div>
        </div>
      )}

      {openTasks.length === 0 ? (
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('task.none')}</div>
      ) : (
        <div style={{ display: 'grid', gap: 5 }}>
          {openTasks.map((tk) => (
            <div key={tk.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 'var(--fs-xs)', background: 'var(--surface-2)', borderRadius: 8, padding: '6px 8px' }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <b>{tk.title}</b>{tk.note ? ` — ${tk.note}` : ''}
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}> · {tk.department_name ? <><Building2 size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {tk.department_name}</> : tk.team_name ? <><Users size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {tk.team_name}</> : <><User size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {tk.assigned_to || '—'}</>}{tk.due_date ? ` · ${new Date(tk.due_date).toLocaleDateString(localeUI())}` : ''}</span>
              </span>
              <button className="btn ghost sm" onClick={() => done(tk.id)} title={t('task.done')}><Check size={13} strokeWidth={1.75} /></button>
            </div>
          ))}
        </div>
      )}

      {/* Tareas HECHAS: colapsable, para consultar lo ya cerrado sin perderlo de vista. */}
      {doneTasks.length > 0 && (
        <div style={{ borderTop: '1px dashed var(--line)', paddingTop: 6 }}>
          <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)', padding: '2px 6px' }} onClick={() => setShowDone((v) => !v)}>
            {showDone ? '▾' : '▸'} <Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('task.doneTasks')} ({doneTasks.length})
          </button>
          {showDone && (
            <div style={{ display: 'grid', gap: 5, marginTop: 5 }}>
              {doneTasks.map((tk) => (
                <div key={tk.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 'var(--fs-xs)', background: 'var(--surface-2)', borderRadius: 8, padding: '6px 8px', opacity: 0.75 }}>
                  <span style={{ color: 'var(--green)' }}><Check size={13} strokeWidth={1.75} /></span>
                  <span style={{ flex: 1, minWidth: 0, textDecoration: 'line-through' }}>
                    <b>{tk.title}</b>{tk.note ? ` — ${tk.note}` : ''}
                    <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}> · {tk.department_name ? <><Building2 size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {tk.department_name}</> : tk.team_name ? <><Users size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {tk.team_name}</> : <><User size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {tk.assigned_to || '—'}</>}{tk.completed_by ? ` · ${t('task.doneBy')} ${tk.completed_by}` : ''}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
